import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const ProductFavoritesContext = createContext();

export const ProductFavoritesProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  // Cargar IDs de favoritos cuando cambia el estado de auth
  useEffect(() => {
    console.log('=== PFAV INIT ===', { isAuthenticated, hasToken: !!token });
    if (isAuthenticated && token) {
      fetch(`${API_URL}/api/product-favorites/ids`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => {
          console.log('=== PFAV IDS RESPONSE ===', data);
          if (data.productIds) setFavoriteIds(new Set(data.productIds));
        })
        .catch(console.error);
    } else {
      console.log('=== PFAV LOCAL INIT ===', { localData: localStorage.getItem('productFavorites') });
      const local = JSON.parse(localStorage.getItem('productFavorites') || '[]');
      setFavoriteIds(new Set(local));
    }
  }, [isAuthenticated, token]);

  const isProductFavorite = useCallback((productId) => {
    return favoriteIds.has(productId);
  }, [favoriteIds]);

  const toggleProductFavorite = useCallback(async (product) => {
    const productId = product._id || product.id;
    const businessId = typeof product.businessId === 'object' ? product.businessId._id : product.businessId;
    const wasFavorite = favoriteIds.has(productId);
    console.log('=== PFAV TOGGLE START ===', { productId, businessId, wasFavorite, isAuthenticated, hasToken: !!token });

    // Optimistic update
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (wasFavorite) next.delete(productId);
      else next.add(productId);
      return next;
    });

    if (!isAuthenticated || !token) {
      const updated = wasFavorite
        ? [...favoriteIds].filter(id => id !== productId)
        : [...favoriteIds, productId];
      console.log('=== PFAV SAVING LOCAL ===', { favoriteIds: [...favoriteIds], productId, updated });
      localStorage.setItem('productFavorites', JSON.stringify(updated));
      return;
    }

    try {
      if (wasFavorite) {
        const res = await fetch(`${API_URL}/api/product-favorites/${productId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('=== PFAV TOGGLE STATUS ===', res.status);
        const data = await res.json();
        console.log('=== PFAV TOGGLE RESPONSE ===', data);
      } else {
        const res = await fetch(`${API_URL}/api/product-favorites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ productId, businessId })
        });
        console.log('=== PFAV TOGGLE STATUS ===', res.status);
        const data = await res.json();
        console.log('=== PFAV TOGGLE RESPONSE ===', data);
      }
    } catch (err) {
      // Revert on error
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (wasFavorite) next.add(productId);
        else next.delete(productId);
        return next;
      });
      console.log('=== PFAV TOGGLE ERROR ===', err);
    }
  }, [favoriteIds, isAuthenticated, token]);

  const contextValue = useMemo(() => ({
    isProductFavorite, toggleProductFavorite, favoriteIds
  }), [isProductFavorite, toggleProductFavorite, favoriteIds]);

  return (
    <ProductFavoritesContext.Provider value={contextValue}>
      {children}
    </ProductFavoritesContext.Provider>
  );
};

export const useProductFavorites = () => useContext(ProductFavoritesContext);
